// BlogPreview.tsx
import React from 'react';
import {
    CardActionArea,
    CardContent,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Blog } from '../../workers/ApiWorker';
import CardMedia from '@mui/material/CardMedia';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import { convertSimple } from '../../helpers/Date';
import { useNavigate } from 'react-router-dom';

interface BlogPreviewProps {
    name: string;
    miniDescription: string;
    date: string | null;
    image: string;
    clickable?: boolean; // ✅ new optional prop
    slug?: string; // ✅ optional path for navigation
}

const BlogPreview: React.FC<BlogPreviewProps> = ({
    name,
    miniDescription,
    date,
    image,
    clickable = false, // ✅ default to false
    slug,
}) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (clickable && slug) {
            navigate(`/blog/${slug}`);
        }
    };

    return (
        <Card
            sx={{
                width: '100%',
                maxWidth: 500, // ✅ sets consistent, narrower card width
                backgroundColor: 'transparent',
                color: 'white',
                borderRadius: 3,
                p: 2,
                transition: 'background-color 0.3s ease',
                '&:hover': {
                    backgroundColor: '#1e3a8a',
                    cursor: 'pointer',
                },
            }}
            onClick={handleClick}
        >
            <CardActionArea
                disableRipple
                disableTouchRipple
                focusRipple={false}
                sx={{
                    borderRadius: 3,
                    '&:hover': { backgroundColor: 'transparent' }, // no hover tint
                    // kill the built-in white overlay
                    '& .MuiCardActionArea-focusHighlight': { opacity: 0 },
                    '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
                }}
            >
                <CardMedia
                    component="img"
                    height="250"
                    image={image}
                    alt={name}
                    sx={{ borderRadius: 3, display: 'block' }}
                />
                <CardContent
                    sx={{
                        pb: 0,
                        '&:last-child': { pb: 0 },
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            mb: 1,
                            textAlign: 'left', // ✅ left align
                            fontWeight: 700,
                        }}
                    >
                        {name}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            mb: 2,
                            textAlign: 'left', // ✅ left align
                            color: 'rgba(255,255,255,0.7)',
                        }}
                    >
                        {miniDescription}
                    </Typography>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Chip
                            label={date ? convertSimple(date) : 'Draft'}
                            sx={{
                                backgroundColor: '#1d3557',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                            }}
                        />
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default BlogPreview;
